import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaTimes, FaPlay, FaPause, FaHeart, FaShare } from 'react-icons/fa';
import { getGallerySlideshow, likePhoto, unlikePhoto, trackPhotoView } from '../services/api';
import ShareMenu from '../components/ShareMenu';
import { toast } from 'react-toastify';

function SlideshowViewer() {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [gallery, setGallery] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [likedPhotos, setLikedPhotos] = useState(new Set());

  const fetchSlideshow = useCallback(async () => {
    try {
      const response = await getGallerySlideshow(galleryId);
      setGallery(response.data.gallery);
      setPhotos(response.data.photos);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching slideshow:', error);
      toast.error('Impossible de charger le diaporama');
      setLoading(false);
    }
  }, [galleryId]);

  const nextPhoto = useCallback(() => {
    if (photos.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  const prevPhoto = useCallback(() => {
    if (photos.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    fetchSlideshow();
  }, [fetchSlideshow]);

  useEffect(() => {
    if (isPlaying && photos.length > 0) {
      const interval = setInterval(() => {
        nextPhoto();
      }, 5000); // 5 seconds per photo
      return () => clearInterval(interval);
    }
  }, [isPlaying, photos.length, nextPhoto]);

  useEffect(() => {
    // Track view when photo changes
    if (photos[currentIndex]) {
      trackPhotoView(photos[currentIndex].id).catch(console.error);
    }
  }, [currentIndex, photos]);

  useEffect(() => {
    // Keyboard navigation
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'Escape') navigate(-1);
      if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying((playing) => !playing);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, nextPhoto, prevPhoto]);

  const handleLike = async () => {
    const photo = photos[currentIndex];
    try {
      if (likedPhotos.has(photo.id)) {
        await unlikePhoto(photo.id);
        setLikedPhotos(prev => {
          const newSet = new Set(prev);
          newSet.delete(photo.id);
          return newSet;
        });
        toast.success("Mention J'aime retirée");
      } else {
        await likePhoto(photo.id);
        setLikedPhotos(prev => new Set(prev).add(photo.id));
        toast.success("Photo ajoutée aux J'aime !");
      }
    } catch (error) {
      console.error('Error liking photo:', error);
      toast.error("Impossible de mettre à jour la mention J'aime");
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '1.5rem'
      }}>
        Chargement du diaporama...
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '1.5rem'
      }}>
        Aucune photo dans cette galerie
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'black',
      zIndex: 9999
    }}>
      {/* Header */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        padding: '20px',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
        zIndex: 10000,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ color: 'white' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{gallery?.name}</h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', opacity: 0.8 }}>
            par {gallery?.photographer}
          </p>
        </div>
        
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            fontSize: '2rem',
            cursor: 'pointer',
            padding: '10px 15px',
            borderRadius: '8px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
        >
          <FaTimes />
        </button>
      </div>

      {/* Main Image */}
      <img
        src={currentPhoto.url}
        alt="Diaporama"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          userSelect: 'none'
        }}
      />

      {/* Side Navigation */}
      <button
        onClick={prevPhoto}
        style={{
          position: 'absolute',
          left: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.2)',
          border: 'none',
          color: 'white',
          fontSize: '2rem',
          padding: '20px 25px',
          borderRadius: '50%',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 10000
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.4)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <FaArrowLeft />
      </button>

      <button
        onClick={nextPhoto}
        style={{
          position: 'absolute',
          right: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.2)',
          border: 'none',
          color: 'white',
          fontSize: '2rem',
          padding: '20px 25px',
          borderRadius: '50%',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          zIndex: 10000
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.4)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <FaArrowRight />
      </button>

      {/* Bottom Controls */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '30px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
        zIndex: 10000
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px'
        }}>
          {/* Left side - Photo info */}
          <div style={{ color: 'white', fontSize: '1.1rem' }}>
            {currentIndex + 1} / {photos.length}
          </div>

          {/* Center - Playback controls */}
          <div style={{
            display: 'flex',
            gap: '15px',
            alignItems: 'center'
          }}>
            <button
              onClick={() => setIsPlaying((playing) => !playing)}
              style={{
                background: 'rgba(255,255,255,0.3)',
                border: 'none',
                color: 'white',
                fontSize: '1.5rem',
                padding: '15px 20px',
                borderRadius: '50%',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.5)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
          </div>

          {/* Right side - Actions */}
          <div style={{
            display: 'flex',
            gap: '15px',
            alignItems: 'center'
          }}>
            <button
              onClick={handleLike}
              style={{
                background: likedPhotos.has(currentPhoto.id) ? '#FF8C42' : 'rgba(255,255,255,0.3)',
                border: 'none',
                color: 'white',
                fontSize: '1.3rem',
                padding: '12px 18px',
                borderRadius: '50%',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!likedPhotos.has(currentPhoto.id)) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!likedPhotos.has(currentPhoto.id)) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                }
              }}
            >
              <FaHeart />
            </button>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowShareMenu((visible) => !visible)}
                style={{
                  background: 'rgba(255,255,255,0.3)',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.3rem',
                  padding: '12px 18px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.5)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              >
                <FaShare />
              </button>

              {showShareMenu && (
                <ShareMenu
                  photoId={currentPhoto.id}
                  photoUrl={currentPhoto.url}
                  onClose={() => setShowShareMenu(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {isPlaying && (
          <div style={{
            marginTop: '20px',
            width: '100%',
            height: '4px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div
              style={{
                height: '100%',
                background: '#FF8C42',
                animation: 'progress 5s linear infinite',
                transformOrigin: 'left'
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}

export default SlideshowViewer;
